/**
 * The subscriber-facing portal API, as the browser sees it.
 *
 * This is the contract radportal (Go, public, unauthenticated, rate-limited)
 * will implement; openapi/radportal.yaml is the same contract for the server
 * side. Until that service is deployed, `mockApi()` answers every call, so
 * the screens can be built, tested and demonstrated. `httpApi()` is what the
 * page uses when config.json names a portalApiBase.
 *
 * Every call is tenant-scoped by (namespace, netid) from the router's query
 * string. There is no token: the guest has none by construction.
 */

export interface PortalTheme {
  /** Hotspot display name, e.g. "Supersonic Wi-Fi". themeDataText.wifiheading. */
  title: string;
  /** Second line under the title. wifiheading1 + wifiheading2. */
  subtitle: string;
  /** Absolute URL of the tenant logo, or null. themeDataImage.hotspotimage. */
  logoUrl: string | null;
  /** One hex colour. themeDataStyle.accent. */
  accent: string;
  /** light | dark | auto. themeDataStyle.mode. */
  mode: "light" | "dark" | "auto";
  /**
   * Present only when the operator has PUBLISHED a portal theme -- its own or
   * its nearest ancestor namespace's. ground (60) and ink (30) join accent (10),
   * and applyTheme grows the three into the whole token set. Absent, the page
   * renders exactly as it did before these fields existed.
   */
  ground?: string;
  ink?: string;
  published?: boolean;
  /**
   * A published theme's page colour, around the card and under a background
   * image. Absent: the ground colour, as before.
   */
  backdrop?: string;
  /** A published theme's layout. Absent: "compact", today's card. */
  layout?: PortalLayout;
  /** A published theme's card opacity in percent (80-100), used over a background image. */
  cardOpacity?: number;
  /** A published theme's logo size. Absent: "medium". */
  logoSize?: PortalLogoSize;
  /**
   * The pages the footer's "Legal & Terms" link lists, in order. radportal
   * names only the ones that exist; absent or empty, there is no link.
   */
  legal?: LegalKind[];
  /**
   * The published theme's page text by catalogue key (copy.ts). radportal
   * sends it only with a published theme; the page re-validates every entry.
   */
  copy?: Record<string, string>;
  /** Sign-up page heading / lead. regheading, regalert. */
  registerHeading: string;
  registerLead: string;
  /**
   * The operator's own `style` rows, by their original attribute name,
   * sanitised server-side (no markup, no @import). Only the names in
   * STYLE_TARGETS are rendered; the rest are carried but unused.
   */
  styles?: Record<string, string>;
  /** Absolute URL of the tenant background image. themeDataImage.backgroundimage. */
  backgroundUrl?: string | null;
  /** Plan filters for the recharge screen. hotspotDataConfig.planlabels / planannotations. */
  planLabels: string;
  planAnnotations: string;
  /**
   * The operator's public contact, shown at the bottom of every page.
   *
   * Absent is the COMMON case, not an error: most namespaces have no contact
   * rows, and radportal folds "no grant", "unknown namespace" and "lookup
   * failed" into the same absence. Render only the lines that have values; with
   * no contact at all the footer is exactly what it was before this existed.
   *
   * 🚨 These seven fields are a privacy boundary agreed all the way down to
   * radaccess's `operatorcontact` RBAC resource, which exists so a public
   * portal can read an address without reaching bank details, PAN, GSTIN or
   * KYC images. Do not widen it.
   */
  contact?: PortalContact;
}

export interface PortalContact {
  address?: string;
  city?: string;
  state?: string;
  pin?: string;
  country?: string;
  phone?: string;
  email?: string;
  /** The operator's website, an https URL: the footer's one link off the portal. */
  website?: string;
}

export type PortalLayout = "compact" | "wide" | "full";

/** How large a published theme draws the logo. Absent: "medium", the size before the choice existed. */
export type PortalLogoSize = "small" | "medium" | "large" | "xlarge";
export const PORTAL_LOGO_SIZES: readonly PortalLogoSize[] = ["small", "medium", "large", "xlarge"];

/** The guest-facing legal pages, in the order they are listed. */
export const LEGAL_KINDS = ["pricing", "terms", "privacy", "refunds"] as const;
export type LegalKind = (typeof LEGAL_KINDS)[number];
export const isLegalKind = (v: unknown): v is LegalKind => (LEGAL_KINDS as readonly unknown[]).includes(v);
/** The pages that are the operator's own text. Pricing is built from the plans on sale. */
export type DocumentKind = Exclude<LegalKind, "pricing">;

export interface PortalPricing {
  plans: PlanOption[];
  /** The operator's pricing note (taxes, fair use), as plain text. */
  note?: string;
}

export interface SubscriberPlan {
  // No `name`: radportal stopped returning the subscriber's first name from
  // this unauthenticated lookup (radportal 02c1010). The page never showed it.
  username: string;
  groupName: string;     // e.g. "wifi-fibre50-499"
  heading: string;       // group_hd, human plan title
  amountPaise: number;   // 0 = free plan
  startDate: string | null;   // ISO
  expiryDate: string | null;  // ISO
  daysToExpiry: number | null; // negative = expired
  /** Subscriber cart (radportal cartView). Absent: today's plan-only screen. */
  cart?: PortalCart;
}

export interface PlanOption {
  groupName: string;
  groupUid: string;
  heading: string;
  amountPaise: number;
  /** As SubscriberPlan.cart, for this option; only the per-mobile options route carries it. */
  cart?: PortalCart;
}

/**
 * The plan screen's cart (radportal app/handler/cart.go cartView). Offered
 * add-ons are recurring ones, as radaccess offers them. It never carries the
 * subscriber's remembered add-ons (the route is unauthenticated): those come
 * back only in a preview or an order's lines.
 */
export interface PortalCart {
  wired: boolean;
  /** The plan spends a PIN. */
  pin: boolean;
  offered: PortalCartOffer[];
  /** The plan alone. */
  totalPaise: number;
}

/** A price component's id: a UUID string, compared as a string. */
export type PortalComponentId = string;

export interface PortalCartOffer {
  componentId: PortalComponentId;
  name: string;
  amountPaise: number;
  /** Extra devices one unit allows; 0 = billing only. */
  devices: number;
}

export interface PortalCartAddon {
  componentId: PortalComponentId;
  quantity: number;
}

/** One priced line (cartLineOut). componentId is absent on the plan-only fallback line. */
export interface PortalCartLine {
  componentId?: PortalComponentId;
  name: string;
  quantity: number;
  amountPaise: number;
}

/** POST plan/{Mobile}/cart: the server's lines and total, remembered add-ons included. */
export interface PortalCartPreview {
  lines: PortalCartLine[];
  totalPaise: number;
}

export interface RechargeOrder {
  provider: "razorpay";
  orderId: string;
  amountPaise: number;
  currency: "INR";
  keyId: string;
  description: string;
  /** Where the checkout returns; the API verifies the signature there. */
  callbackUrl: string;
  /** What the amount buys, remembered add-ons included; absent on a plan-only order. */
  lines?: PortalCartLine[];
}

export type PortalFailure =
  | { kind: "unreachable" }
  | { kind: "not_found" }
  | { kind: "rate_limited"; retryAfter: number | null }
  | { kind: "rejected"; message: string }
  | { kind: "server"; status: number };

export class PortalError extends Error {
  constructor(readonly failure: PortalFailure) {
    super(failure.kind === "rejected" ? failure.message : failure.kind);
  }
}

/** A plan's price with a promotion code (radportal POST …/plan/{mobile}/promocode). */
export interface PromoPrice {
  code: string;
  normalPaise: number;
  offPaise: number;
  amountPaise: number;
}

export interface Tenant {
  namespace: string;
  netid: string;
}

export interface PortalApi {
  theme(t: Tenant, opts?: { template?: string; theme?: string }): Promise<PortalTheme>;
  /** Cookie auto-login: returns the credentials the router should be given, or null. */
  tokenLogin(t: Tenant): Promise<{ username: string; password: string } | null>;
  /**
   * Mint the auto-login token. The PASSWORD is required: radportal checks it
   * before it will mint anything, so a device can only get a token for an
   * account it already proved it knows.
   */
  tokenCreate(t: Tenant, username: string, password: string): Promise<void>;
  tokenRemove(t: Tenant): Promise<void>;
  /** Recharge, step 1. 404 -> not registered. */
  plan(t: Tenant, mobile: string): Promise<SubscriberPlan>;
  /**
   * The live field check: does an account exist for this mobile? One boolean
   * and nothing else. Rejects on 429 (radportal limits this per address) and on
   * any failure -- the caller must treat a rejection as "cannot tell", never as
   * "not registered".
   */
  mobileRegistered(t: Tenant, mobile: string): Promise<boolean>;
  planOptions(t: Tenant, mobile: string): Promise<PlanOption[]>;
  /** Recharge, step 2. Creates the gateway order for the chosen (or current) plan. */
  /**
   * The price of a plan with a promotion code, before paying (owner 2026-10-14,
   * design §9.18). Writes nothing. "rejected" carries why in the guest's words.
   */
  promoCode(t: Tenant, mobile: string, body: { code: string; groupName: string }): Promise<PromoPrice>;
  rechargeOrder(t: Tenant, body: { mobile: string; groupName: string; groupUid?: string; returnUrl: string; code?: string; addons?: PortalCartAddon[] }): Promise<RechargeOrder>;
  /**
   * The cart's total as add-ons are ticked, with the promotion code when one
   * is applied: the Pay amount. Reserves nothing, makes no order.
   */
  cartPreview(t: Tenant, mobile: string, body: { plan: string; addons: PortalCartAddon[]; code?: string }): Promise<PortalCartPreview>;
  /**
   * Recharge, step 3. Verifies the payment and applies the recharge. Razorpay
   * returns ids and a signature; Cashfree returns only its order id (the
   * server asks Cashfree whether it was paid), so paymentId/signature are "".
   */
  rechargeVerify(t: Tenant, body: { orderId: string; paymentId: string; signature: string }): Promise<{ ok: boolean; message: string }>;
  /** Sign-up: creates the account and sends the SMS code. 409 -> already registered. */
  register(t: Tenant, body: { mobile: string; name: string; password: string; acceptedTerms: true }): Promise<{ pending: true }>;
  registerVerify(t: Tenant, body: { mobile: string; code: string }): Promise<{ ok: boolean; message: string }>;
  registerResend(t: Tenant, mobile: string): Promise<void>;
  /** Password reset by SMS code. */
  resetRequest(t: Tenant, mobile: string): Promise<void>;
  resetConfirm(t: Tenant, body: { mobile: string; code: string; password: string }): Promise<{ ok: boolean; message: string }>;
  /** My plan: mobile + password -> the plan status rows. */
  planStatus(t: Tenant, body: { mobile: string; password: string }): Promise<Array<[string, string]>>;
  /** One of the operator's legal documents. 404 -> not_found. */
  legalDocument(t: Tenant, kind: DocumentKind): Promise<{ kind: DocumentKind; body: string }>;
  /** The plans on sale here and the operator's pricing note. Needs no mobile: prices are public. */
  pricing(t: Tenant): Promise<PortalPricing>;
}

const JSON_HEADERS = { "content-type": "application/json", accept: "application/json" };

/** Talks to radportal at `base`. Never sends a credential; relies on cookies for the auto-login token. */
export function httpApi(base: string): PortalApi {
  const root = base.replace(/\/+$/, "");
  const scope = (t: Tenant) => `${root}/portal/${encodeURIComponent(t.namespace)}/${encodeURIComponent(t.netid)}`;

  async function call<T>(url: string, init: RequestInit & { method: string }): Promise<T> {
    let res: Response;
    try {
      res = await fetch(url, { ...init, headers: { ...JSON_HEADERS, ...(init.headers ?? {}) }, credentials: "include" });
    } catch {
      throw new PortalError({ kind: "unreachable" });
    }
    if (res.status === 404) throw new PortalError({ kind: "not_found" });
    if (res.status === 429) {
      const ra = Number(res.headers.get("retry-after"));
      throw new PortalError({ kind: "rate_limited", retryAfter: Number.isFinite(ra) && ra > 0 ? ra : null });
    }
    if (res.status === 400 || res.status === 409 || res.status === 422) {
      let message = `Request rejected (${res.status})`;
      try {
        const body = (await res.json()) as { error?: string; message?: string };
        message = body.message ?? body.error ?? message;
      } catch {
        /* keep the default */
      }
      throw new PortalError({ kind: "rejected", message });
    }
    if (!res.ok) throw new PortalError({ kind: "server", status: res.status });
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  const get = <T>(url: string) => call<T>(url, { method: "GET" });
  const post = <T>(url: string, body: unknown) => call<T>(url, { method: "POST", body: JSON.stringify(body) });

  return {
    theme: (t, opts) => {
      const q = new URLSearchParams();
      if (opts?.template) q.set("template", opts.template);
      if (opts?.theme) q.set("theme", opts.theme);
      const qs = q.toString();
      return get(`${scope(t)}/theme${qs ? `?${qs}` : ""}`);
    },
    tokenLogin: async (t) => {
      try {
        return await get(`${scope(t)}/token`);
      } catch (e) {
        if (e instanceof PortalError && e.failure.kind === "not_found") return null;
        throw e;
      }
    },
    tokenCreate: (t, username, password) => post(`${scope(t)}/token`, { username, password }),
    tokenRemove: (t) => call(`${scope(t)}/token`, { method: "DELETE" }),
    plan: (t, mobile) => get(`${scope(t)}/plan/${encodeURIComponent(mobile)}`),
    planOptions: (t, mobile) => get(`${scope(t)}/plan/${encodeURIComponent(mobile)}/options`),
    mobileRegistered: async (t, mobile) =>
      (await get<{ registered: boolean }>(`${scope(t)}/mobile/${encodeURIComponent(mobile)}`)).registered === true,
    promoCode: (t, mobile, body) => post(`${scope(t)}/plan/${encodeURIComponent(mobile)}/promocode`, body),
    rechargeOrder: (t, body) => post(`${scope(t)}/recharge/order`, body),
    cartPreview: (t, mobile, body) => post(`${scope(t)}/plan/${encodeURIComponent(mobile)}/cart`, body),
    rechargeVerify: (t, body) => post(`${scope(t)}/recharge/verify`, body),
    register: (t, body) => post(`${scope(t)}/register`, body),
    registerVerify: (t, body) => post(`${scope(t)}/register/verify`, body),
    registerResend: (t, mobile) => post(`${scope(t)}/register/resend`, { mobile }),
    resetRequest: (t, mobile) => post(`${scope(t)}/reset/request`, { mobile }),
    resetConfirm: (t, body) => post(`${scope(t)}/reset/confirm`, body),
    planStatus: (t, body) => post(`${scope(t)}/plan/status`, body),
    legalDocument: (t, kind) => get(`${scope(t)}/legal/${encodeURIComponent(kind)}`),
    pricing: (t) => get(`${scope(t)}/pricing`),
  };
}

/**
 * An in-memory stand-in with the same shape, for development and tests.
 * Deterministic: a mobile ending in 1 or 9 is registered (9 with an expired
 * plan); any other ending is not, so sign-up accepts it and recharge refuses
 * it. The SMS code is always 123456.
 */
export function mockApi(delayMs = 150): PortalApi {
  const wait = <T>(v: T): Promise<T> => new Promise((r) => setTimeout(() => r(v), delayMs));
  const fail = (f: PortalFailure): Promise<never> => new Promise((_, rej) => setTimeout(() => rej(new PortalError(f)), delayMs));
  const registered = (mobile: string) => /[19]$/.test(mobile);
  const offered: PortalCartOffer[] = [
    { componentId: "6f1c3a52-0d2e-4b7a-9c11-3e5f7a9b0c21", name: "Router rental", amountPaise: 9900, devices: 0 },
    { componentId: "6f1c3a52-0d2e-4b7a-9c11-3e5f7a9b0c23", name: "Extra login", amountPaise: 4990, devices: 1 },
  ];
  const wired = (amountPaise: number): PortalCart => ({ wired: true, pin: false, offered, totalPaise: amountPaise });
  const plans: PlanOption[] = [
    { groupName: "wifi-fibre50-499", groupUid: "g1", heading: "Fibre 50 · 30 days · 50 Mbps · unlimited", amountPaise: 49900, cart: wired(49900) },
    { groupName: "wifi-fibre100-799", groupUid: "g2", heading: "Fibre 100 · 30 days · 100 Mbps · unlimited", amountPaise: 79900, cart: wired(79900) },
    { groupName: "wifi-day-49", groupUid: "g3", heading: "Day pass · 24 hours · 20 Mbps · 5 GB", amountPaise: 4900, cart: { wired: false, pin: false, offered: [], totalPaise: 4900 } },
  ];
  // The stand-in server's quote (the real one is radaccess's): a mobile ending
  // in 1 keeps a remembered router rental.
  const mockLines = (mobile: string, groupName: string, addons: PortalCartAddon[] = [], code?: string): PortalCartLine[] => {
    const p = plans.find((x) => x.groupName === groupName) ?? plans[0]!;
    const lines: PortalCartLine[] = [{ componentId: `plan-${p.groupUid}`, name: p.heading.split(" · ")[0]!, quantity: 1, amountPaise: p.amountPaise }];
    // The stand-in's one code, SAVE100, as promoCode answers it: ₹100 off the plan.
    if (code && code.toUpperCase() === "SAVE100") lines.push({ name: "Code SAVE100", quantity: 1, amountPaise: -10000 });
    if (!p.cart?.wired) return lines;
    const want = new Map(addons.map((a) => [a.componentId, a.quantity]));
    if (mobile.endsWith("1") && !want.has(offered[0]!.componentId)) want.set(offered[0]!.componentId, 1);
    for (const o of offered) {
      const n = want.get(o.componentId);
      if (n) lines.push({ componentId: o.componentId, name: o.name, quantity: n, amountPaise: o.amountPaise * n });
    }
    return lines;
  };
  const sum = (lines: PortalCartLine[]) => lines.reduce((a, l) => a + l.amountPaise, 0);
  let tokenUser: string | null = null;
  return {
    theme: (t) =>
      wait({
        title: `${t.namespace.split(".")[1] ?? "Guest"} Wi-Fi`.replace(/^./, (c) => c.toUpperCase()),
        subtitle: "Free Wi-Fi for guests. Sign in with your mobile to begin.",
        logoUrl: null,
        accent: "#E3A02C",
        mode: "auto",
        registerHeading: "Create your Wi-Fi account",
        registerLead: "Your mobile number is your login. We verify it by SMS.",
        planLabels: "wifi",
        planAnnotations: "paygw",
        legal: [...LEGAL_KINDS],
      }),
    tokenLogin: () => wait(tokenUser ? { username: tokenUser, password: "mock" } : null),
    tokenCreate: (_t, username, password) => {
      if (!password) return fail({ kind: "rejected", message: "Wrong mobile number or password." });
      tokenUser = username;
      return wait(undefined);
    },
    tokenRemove: () => {
      tokenUser = null;
      return wait(undefined);
    },
    plan: (_t, mobile) => {
      if (!registered(mobile)) return fail({ kind: "not_found" });
      const expired = mobile.endsWith("9");
      return wait({
        username: mobile,
        groupName: plans[0]!.groupName,
        heading: plans[0]!.heading,
        amountPaise: plans[0]!.amountPaise,
        startDate: "2026-08-05",
        expiryDate: expired ? "2026-09-03" : "2026-10-05",
        daysToExpiry: expired ? -5 : 27,
        cart: plans[0]!.cart!,
      });
    },
    planOptions: () => wait(plans),
    mobileRegistered: (_t, mobile) => wait(registered(mobile)),
    promoCode: (_t, _mobile, body) => {
      const normal = plans.find((p) => p.groupName === body.groupName)?.amountPaise ?? 49900;
      return body.code.toUpperCase() === "SAVE100"
        ? wait({ code: "SAVE100", normalPaise: normal, offPaise: 10000, amountPaise: normal - 10000 })
        : fail({ kind: "rejected", message: "This code is not valid." });
    },
    cartPreview: (_t, mobile, body) => {
      const lines = mockLines(mobile, body.plan, body.addons, body.code);
      return wait({ lines, totalPaise: sum(lines) });
    },
    rechargeOrder: (_t, body) =>
      wait({
        provider: "razorpay" as const,
        orderId: "order_mock_" + body.groupName,
        amountPaise: sum(mockLines(body.mobile, body.groupName, body.addons, body.code)),
        lines: mockLines(body.mobile, body.groupName, body.addons, body.code),
        currency: "INR" as const,
        keyId: "rzp_test_mock",
        description: body.groupName,
        callbackUrl: body.returnUrl,
      }),
    rechargeVerify: () => wait({ ok: true, message: "Your recharge is complete." }),
    register: (_t, body) => (registered(body.mobile) ? fail({ kind: "rejected", message: "This mobile number is already registered." }) : wait({ pending: true as const })),
    registerVerify: (_t, body) => wait(body.code === "123456" ? { ok: true, message: "Your account is ready. Sign in to get online." } : { ok: false, message: "That code is not right. Check the SMS and try again." }),
    registerResend: () => wait(undefined),
    resetRequest: (_t, mobile) => (mobile.endsWith("0") ? fail({ kind: "not_found" }) : wait(undefined)),
    resetConfirm: (_t, body) => wait(body.code === "123456" ? { ok: true, message: "Password changed. Sign in with your new password." } : { ok: false, message: "That code is not right." }),
    planStatus: (_t, body) =>
      body.password === "bad"
        ? fail({ kind: "rejected", message: "Wrong mobile number or password." })
        : wait([
            ["Plan", "Fibre 50"],
            ["Start", "05-08-2026"],
            ["Expiry", "05-10-2026"],
            ["Data used", "1.2 GB"],
          ]),
    legalDocument: (_t, kind) =>
      wait({
        kind,
        body:
          `Sample text. Your own ${kind === "terms" ? "Terms of Service" : kind === "privacy" ? "Privacy Policy" : "Cancellation & Refunds policy"} ` +
          "is written in the console under Legal & Terms and shown here.\n\nA blank line starts a new paragraph.",
      }),
    pricing: () => wait({ plans, note: "Prices include GST.\nPlans start when you first sign in." }),
  };
}
